import PropTypes from "prop-types";
import React, { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { jwtDecode } from "jwt-decode";
import { Link, Title } from "@yoast/ui-library";
import { doLoginRequest, prepareInternalRequest } from "shared-frontend/functions/api";
import { setLoginPreCookie } from "shared-frontend/functions/loginPreCookie";
import { performPasskeyAuthentication, toPasskeyLoginError } from "shared-frontend/functions/passkeys";
import { login } from "shared-frontend/redux/actions/user";
import { TWO_FACTOR_ERROR_MESSAGE } from "shared-frontend/redux/actions/login";
import Login from "../login/Login";
import LoginLogo from "../login/LoginLogo";
import * as styles from "./ConsentScreenStyles.module.scss";

/**
 * Normalizes an error value to the `{ message, type }` shape that
 * `ErrorDisplay` (and `ErrorPropTypeShape`) expects. Plain strings, plain
 * Errors and already-shaped objects all collapse to the same structure.
 *
 * @param {string|Object|Error|null} err The raw error.
 *
 * @returns {{ message: string, type: string }|null} The normalized error or null.
 */
const normalizeError = ( err ) => {
	if ( ! err ) {
		return null;
	}
	if ( typeof err === "string" ) {
		return { message: err, type: "error" };
	}
	if ( err.message ) {
		return { message: err.message, type: err.type || "error" };
	}
	return { message: "Something went wrong. Please try again.", type: "error" };
};

/**
 * Credential step rendered inside the consent screen.
 *
 * Covers two scenarios with the same UI: the user isn't logged in yet, or the
 * OIDC client requested explicit re-authentication via `prompt=login` (reason
 * `login_prompt`). Either way we render an in-card login form so the user
 * never leaves the consent page — that's what keeps the silent-login iframe
 * in `index.jsx` from firing on a missing JWT.
 *
 * Owns the credential-form state (submitting, error, requireOTP) and the
 * MyYoast login plumbing: `POST /Customers/login-user/`, the JWT decode,
 * the Redux `login()` dispatch, and the 2FA error-string detection. Once
 * authentication succeeds it calls `onAuthenticated()` to hand the flow back
 * to `useOAuthInteraction`, which knows how to resolve the OIDC interaction
 * and follow the resume URL.
 *
 * Reuses the standalone `Login` component for the email/password/OTP/Turnstile
 * form. `Login`'s `oauthError`/`loggedIn` effect (`Login.jsx:30-34`) only fires
 * when BOTH are truthy; we pass them as `false` so it stays asleep.
 *
 * The form's `rememberMe` checkbox is shown for parity with /login but has no
 * effect here: `/oauth/interaction/:uid/login` hardcodes `remember: false` and
 * `/Customers/login-user` does not vary cookie TTL on it.
 *
 * @param {Object}      props                  The component props.
 * @param {string|null} props.clientName       Name of the OIDC client driving this flow, used in the
 *                                             helper copy. Falls back to "this application" while the
 *                                             interaction details are still loading.
 * @param {Function}    props.onAuthenticated  Called after a successful MyYoast login. Expected to
 *                                             resolve the OIDC interaction and follow the resume URL.
 *                                             Must resolve to `true` when a redirect was initiated
 *                                             and `false` when the resolver failed — see
 *                                             `useOAuthInteraction#resolveInteractionAfterLogin`.
 *
 * @returns {React.ReactElement} The credential step.
 */
function ConsentCredentialReauth( { clientName = null, onAuthenticated } ) {
	const [ submitting, setSubmitting ] = useState( false );
	const [ error, setError ] = useState( null );
	const [ requireOTP, setRequireOTP ] = useState( false );
	const dispatch = useDispatch();

	/**
	 * Completes a successful MyYoast login: reflects the user in Redux and hands
	 * the flow back to the OIDC interaction resolver.
	 *
	 * Reflect the (possibly different) user in Redux. The resume redirect
	 * re-bootstraps the SPA, which will fetchUser() — no need here.
	 *
	 * Guard against a JWT that doesn't carry `accountId`: dispatching
	 * `login( token, undefined )` would silently poison Redux state until
	 * the resume redirect completes. Treat it as a login failure instead.
	 *
	 * `onAuthenticated` owns its own error surface (the hook's `error`,
	 * rendered above the credential form in `ConsentScreen`) and does
	 * NOT throw, so its failures don't double-render through the
	 * component's local `error` state.
	 *
	 * It returns `true` when a redirect was initiated, `false` when the
	 * resolver failed. On failure we unlock the form so the user can
	 * see the hook-level error and retry; on success we leave it locked
	 * because navigation is imminent and any flash of an enabled form
	 * would let the user re-submit during the transition.
	 */
	const completeLogin = useCallback( async( accessToken ) => {
		const decoded = jwtDecode( accessToken );
		if ( ! decoded || ! decoded.accountId ) {
			throw new Error( "Login failed. Please try again." );
		}
		dispatch( login( accessToken, decoded.accountId ) );

		const redirected = await onAuthenticated();
		if ( ! redirected ) {
			setSubmitting( false );
		}
	}, [ dispatch, onAuthenticated ] );

	const attemptLogin = useCallback( async( { email, password, otp, turnstileToken } ) => {
		setSubmitting( true );
		setError( null );

		try {
			setLoginPreCookie();
			const request = prepareInternalRequest(
				"Customers/login-user/",
				"POST",
				{ email, password, otp, turnstileToken },
				{ credentials: "include" },
			);
			const { access_token: accessToken } = await doLoginRequest( request );
			await completeLogin( accessToken );
		} catch ( err ) {
			if ( err && err.message === TWO_FACTOR_ERROR_MESSAGE ) {
				setRequireOTP( true );
			}
			setError( normalizeError( err ) || normalizeError( "Login failed. Please try again." ) );
			setSubmitting( false );
		}
	}, [ completeLogin ] );

	const attemptPasskeyLogin = useCallback( async( { turnstileToken } ) => {
		setSubmitting( true );
		setError( null );

		try {
			// The verify step sets the `access_token` cookie and returns the JWT
			// in the body, so the login completes without the OAuth iframe dance.
			const { access_token: accessToken } = await performPasskeyAuthentication( { turnstileToken } );
			await completeLogin( accessToken );
		} catch ( err ) {
			setError( toPasskeyLoginError( err ) );
			setSubmitting( false );
		}
	}, [ completeLogin ] );

	const targetDescription = clientName ? <strong>{ clientName }</strong> : "this application";
	return (
		<>
			<LoginLogo />
			<Title as="h1" size={ "1" } className={ styles.title }>
				Log in to continue
			</Title>
			<p className={ styles.reauthHelper }>
				Sign in to your MyYoast account to authorize { targetDescription }.
			</p>
			<div className={ styles.reauthForm }>
				<Login
					error={ error }
					loading={ submitting }
					requireOTP={ requireOTP }
					loggedIn={ false }
					oauthError={ false }
					attemptLogin={ attemptLogin }
					attemptPasskeyLogin={ attemptPasskeyLogin }
				/>
			</div>
			{ /* Root-relative href (not a router push): the new tab does a fresh SPA
			     load of /signup, which renders the signup page while logged out. The
			     OAuth interaction is left intact in this tab. */ }
			<p className={ styles.reauthSignup }>
				Don&apos;t have an account?{ " " }
				<Link href="/signup" target="_blank" rel="noopener noreferrer">
					Create an account
				</Link>
			</p>
		</>
	);
}

ConsentCredentialReauth.propTypes = {
	clientName: PropTypes.string,
	onAuthenticated: PropTypes.func.isRequired,
};

export default ConsentCredentialReauth;
